import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Logo from './Logo';
import StoreButtons from './StoreButtons';
// Brand icons removed from lucide-react v1 — using inline SVGs
const FacebookIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>;
const XIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
const InstagramIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></svg>;
const YoutubeIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" /></svg>;
import { apiCall } from '../../services/apiUtil';

// Exact routes that require authentication
const PROTECTED_ROUTES = new Set([
  '/tournaments', '/squads', '/teams', '/marketplace',
  '/social-feed', '/tournaments', '/squads',
]);

// Route prefixes that require authentication
const PROTECTED_PREFIXES = ['/sport/'];

const Footer = () => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null); // 'success' | 'error'
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || subscribing) return;

    setSubscribing(true);
    setSubscribeStatus(null);
    try {
      const result = await apiCall('/newsletter/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email, source: 'footer' })
      });
      setSubscribeStatus('success');
      setSubscribeMessage(result.message || 'Subscribed!');
      setEmail('');
    } catch (err) {
      setSubscribeStatus('error');
      setSubscribeMessage('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const footerSections = [
    {
      title: "Sports",
      links: [
        { name: "Basketball", href: "/sport/basketball" },
        { name: "Volleyball", href: "/sport/volleyball" },
        { name: "Pickleball", href: "/sport/pickleball" },
        { name: "Cricket", href: "/sport/cricket" }
      ]
    },
    {
      title: "Features",
      links: [
        { name: "Tournaments", href: "/tournaments" },
        { name: "Squads", href: "/squads" },
        { name: "Teams", href: "/teams" },
        { name: "Marketplace", href: "/marketplace" }
      ]
    },
    {
      title: "Community",
      links: [
        { name: "Social Feed", href: "/social-feed" },
        { name: "Tournaments", href: "/tournaments" },
        { name: "Squads", href: "/squads" }
      ]
    },
    {
      title: "Support",
      links: [
        { name: "Help Center", href: "/help" },
        { name: "Contact Us", href: "/contact" },
        { name: "Cookie Policy", href: "/cookies" },
        { name: "Privacy Policy", href: "/privacy" }
      ]
    }
  ];

  // Every one of these was href="#", so all four icons were live-looking links
  // that went nowhere: a visitor got a jump to the top of the page and no
  // explanation, and a crawler got four self-referencing links.
  //
  // A channel we do not have yet still SHOWS — the row of icons is part of the
  // footer's shape — but it renders as a button, not a link, and says so when
  // tapped. That distinction is the point: an <a href> is a promise that
  // somewhere exists to go, and only the accounts that exist get to make it.
  // Filling one in later is a one-line change.
  const socialLinks = [
    { icon: XIcon, href: "https://x.com/KynaixSports", label: "X" },
    { icon: FacebookIcon, href: null, label: "Facebook" },
    { icon: InstagramIcon, href: null, label: "Instagram" },
    { icon: YoutubeIcon, href: null, label: "YouTube" }
  ];

  const isProtected = (href) =>
    PROTECTED_ROUTES.has(href) || PROTECTED_PREFIXES.some((p) => href.startsWith(p));

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    if (isProtected(href) && !isAuthenticated) {
      if (window.location.pathname === '/signin') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/signin', { state: { from: href } });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <footer className={`${theme.backgroundSecondary} ${theme.text} py-4 md:py-8 mt-2 md:mt-6 border-t ${theme.border}`}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-8">
          {/* Logo and Description */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-6">
              <Logo size="default" className="text-white" />
            </div>
            <p className={`${theme.textMuted} mb-6`}>
              Your ultimate sports community. Connect, compete, and excel in your favorite sports with Kynaix Sports.
            </p>

            {/* Social Links */}
            <div className="flex gap-x-4">
              {socialLinks.map(({ icon: Icon, href, label }) => {
                const shape = `w-10 h-10 ${theme.card} rounded-full flex items-center justify-center ${theme.primaryHover} transition-colors`;
                return href ? (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={shape}
                    aria-label={label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ) : (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toast(`${label} is coming soon`)}
                    className={shape}
                    aria-label={`${label} — coming soon`}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className={`font-semibold text-lg mb-4 ${theme.text}`}>{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      onClick={(e) => handleLinkClick(e, link.href)}
                      className={`${theme.textMuted} hover:${theme.text} transition-colors`}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* App Download */}
        <div className={`border-t ${theme.border} pt-8 mb-8`}>
          <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>Get the App</h3>
          <p className={`${theme.textMuted} mb-4 text-sm`}>Available on iOS and Android for the best experience.</p>
          <StoreButtons />
        </div>

        {/* Newsletter Signup */}
        <div className={`border-t ${theme.border} pt-8 mb-8`}>
          <div className="max-w-md">
            <h3 className={`text-lg font-semibold mb-2 ${theme.text}`}>Stay Updated</h3>
            {isAuthenticated ? (
              <p className={`${theme.textMuted} mb-4`}>
                Manage your newsletter subscription from your <a href="/profile/edit?tab=notifications" onClick={(e) => handleLinkClick(e, '/profile/edit?tab=notifications')} className="text-blue-500 hover:text-blue-600 font-medium whitespace-nowrap">Profile settings</a>.
              </p>
            ) : (
              <>
                <p className={`${theme.textMuted} mb-4`}>Get the latest sports news and updates.</p>
                <form onSubmit={handleSubscribe} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className={`flex-1 px-4 py-2 ${theme.input} ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  <button
                    type="submit"
                    disabled={subscribing}
                    className={`${theme.gradient} text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50`}
                  >
                    {subscribing ? '...' : 'Subscribe'}
                  </button>
                </form>
                {subscribeStatus && (
                  <p className={`text-sm mt-2 ${subscribeStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                    {subscribeMessage}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Copyright Notice */}
        <div className={`border-t ${theme.border} pt-6 mb-6`}>
          <p className={`${theme.textMuted} text-xs leading-relaxed`}>
            <span className="font-semibold">Copyright Notice</span><br />
            &copy; 2026 Kynaix LLC. All Rights Reserved.<br />
            All content included on this site, such as text, graphics, logos, button icons, images, audio clips, digital downloads, data compilations, and software, is the property of Kynaix LLC or its content suppliers and is protected by United States and international copyright laws. The compilation of all content on this site is the exclusive property of Kynaix LLC, with copyright authorship for this collection by Kynaix LLC, and is protected by U.S. and international copyright laws.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className={`border-t ${theme.border} pt-6 flex flex-col md:flex-row justify-between items-center`}>
          <p className={`${theme.textMuted} text-sm text-center md:text-left`}>
            &copy; 2026 Kynaix LLC. All Rights Reserved.
          </p>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 md:mt-0">
            <a href="/terms" className={`${theme.textMuted} hover:${theme.text} text-sm`}>Terms of Service</a>
            <a href="/privacy" className={`${theme.textMuted} hover:${theme.text} text-sm`}>Privacy Policy</a>
            <a href="/cookies" className={`${theme.textMuted} hover:${theme.text} text-sm`}>Cookie Policy</a>
            <a href="/unsubscribe" className={`${theme.textMuted} hover:${theme.text} text-sm`}>Unsubscribe</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
